'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { URL } from '@/config/constants';
import { cn } from '@/lib/utils';

export type NavItem = { label: string; href: string };
export type MobileNavItem = {
  label: string;
  href: string;
  target?: '_self' | '_blank';
};
export type MobileNavSection = {
  id: string;
  label: string;
  items: MobileNavItem[];
};

const ICON_LINK_CLASS =
  'p-club-8 text-club-gray-600 hover:text-club-gray-800 transition-colors';

/** PCナビ項目のホバー時アンダーライン */
const PC_NAV_ITEM_HOVER_CLASS =
  "relative flex h-full items-center overflow-visible after:absolute after:-bottom-7 after:left-0 after:right-0 after:h-[1px] after:bg-black after:content-[''] after:opacity-0 hover:after:opacity-100 after:transition-opacity";

export const HEADER_NAV_ITEMS: NavItem[] = [
  { label: '商品一覧', href: URL.ec.products.top },
  { label: 'Medullaとは', href: 'https://medulla.co.jp/about' },
  { label: 'マガジン', href: URL.magazines },
  { label: 'ギフト', href: URL.lp.gift() },
  { label: 'FAQ', href: URL.faq },
];

export const MOBILE_MENU_PRODUCT_ITEMS: MobileNavItem[] = [
  {
    label: 'ヘアケア製品',
    href: 'https://ec.medulla.jp/shop/product_categories/haircare',
  },
  {
    label: 'デバイス',
    href: 'https://ec.medulla.jp/shop/product_categories/beautydevice',
  },
  {
    label: '期間限定',
    href: 'https://ec.medulla.jp/shop/product_categories/limited',
  },
  {
    label: 'スキンケア製品',
    href: 'https://ec.medulla.jp/shop/product_categories/skincare',
  },
  {
    label: 'ブラシ＆ツール',
    href: 'https://ec.medulla.jp/shop/product_categories/category_item',
  },
];

export const MOBILE_MENU_ABOUT_ITEMS: MobileNavItem[] = [
  {
    label: 'メデュラについて',
    href: 'https://medulla.co.jp/about',
  },
  {
    label: 'カウンセリング',
    href: URL.onlineCounseling,
  },
  {
    label: '研究・開発',
    href: 'https://medulla.co.jp/medulla_labo',
  },
];

export const MOBILE_MENU_SECTIONS: MobileNavSection[] = [
  {
    id: 'products',
    label: '商品一覧',
    items: MOBILE_MENU_PRODUCT_ITEMS,
  },
  {
    id: 'about',
    label: 'MEDULLAとは',
    items: MOBILE_MENU_ABOUT_ITEMS,
  },
];

export const MOBILE_MENU_LINK_ITEMS: MobileNavItem[] = [
  { label: 'マガジン', href: URL.magazines },
  { label: 'ギフト', href: URL.lp.gift() },
  { label: 'FAQ / お客さまサポート', href: URL.faq, target: '_blank' },
];

/** SP用: ハンバーガーアイコン */
function HamburgerIcon({
  className,
  open,
}: {
  className?: string;
  open?: boolean;
}) {
  const lineBase =
    'absolute left-1/2 top-1/2 h-[1px] w-8 bg-current transition-transform duration-200 ease-out -translate-x-1/2 -translate-y-1/2';

  return (
    <span className={`relative block w-8 h-8 ${className ?? ''}`} aria-hidden>
      <span
        className={
          open ? `${lineBase} rotate-[15deg]` : `${lineBase} -translate-y-[4px]`
        }
      />
      <span
        className={
          open ? `${lineBase} rotate-[-15deg]` : `${lineBase} translate-y-[2px]`
        }
      />
    </span>
  );
}

/** ロゴ（PC/SP共通） */
function HeaderLogo({ imageClassName }: { imageClassName?: string }) {
  return (
    <Link
      href={URL.officialWebsite}
      className="block leading-none shrink-0"
      aria-label="MEDULLA Brand Site"
    >
      <Image
        src="/images/common/logo-04.svg"
        width={250}
        height={32}
        alt="MEDULLA"
        className={
          imageClassName ?? 'h-4 w-auto md:w-[250px] md:h-auto object-contain'
        }
      />
    </Link>
  );
}

/** ショップアイコン（PC/SP共通） */
function HeaderShopIcon({ className }: { className?: string }) {
  return (
    <Link
      href={URL.ec.products.top}
      className={cn(ICON_LINK_CLASS, className)}
      aria-label="ショップ"
    >
      <Image
        src="/icon/club-icon/bag.svg"
        width={20}
        height={20}
        alt=""
        className="w-5 h-5"
      />
    </Link>
  );
}

/** マイページアイコン */
function HeaderPersonIcon({ onClick }: { onClick?: () => void }) {
  return (
    <button
      type="button"
      className={ICON_LINK_CLASS}
      aria-label="マイページ"
      onClick={onClick}
    >
      <Image
        src="/icon/club-icon/person.svg"
        width={20}
        height={20}
        alt=""
        className="w-5 h-5"
      />
    </button>
  );
}

/** クローズアイコン */
function HeaderCloseIcon({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      className={ICON_LINK_CLASS}
      aria-label="メニューを閉じる"
      onClick={onClick}
    >
      <Image
        src="/icon/club-icon/Close.svg"
        width={20}
        height={20}
        alt=""
        className="w-5 h-5"
      />
    </button>
  );
}

/** PC用: マイページメニューのホバートリガー（isOpen は aria-expanded でアクセシビリティに使用） */
function PcMyPageMenuTrigger({
  isOpen,
  onMouseEnter,
  onMouseLeave,
  className,
}: {
  isOpen: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  className?: string;
}) {
  return (
    <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <button
        type="button"
        className={cn(ICON_LINK_CLASS, className)}
        aria-label="マイページ"
        aria-expanded={isOpen}
      >
        <Image
          src="/icon/club-icon/person.svg"
          width={20}
          height={20}
          alt=""
          className="w-5 h-5"
        />
      </button>
    </div>
  );
}

/** ヘッダー上段とナビ */
export function HeaderNav({
  navItems,
  isSpMenuOpen,
  isPcMyPageMenuOpen,
  onToggleSpMenu,
  onPcMyPageMenuEnter,
  onPcMyPageMenuLeave,
  onSpMyPageMenuClick,
  onCloseSpMyPageMenu,
}: {
  navItems: NavItem[];
  isSpMenuOpen: boolean;
  isPcMyPageMenuOpen: boolean;
  onToggleSpMenu: () => void;
  onPcMyPageMenuEnter?: () => void;
  onPcMyPageMenuLeave?: () => void;
  onSpMyPageMenuClick?: () => void;
  onCloseSpMyPageMenu?: () => void;
}) {
  return (
    <>
      {/* ロゴ（PC） */}
      <div className="hidden md:block flex-shrink-0 md:ml-club-8 md:self-center">
        <HeaderLogo imageClassName="h-4 w-auto md:w-[250px] md:h-auto object-contain" />
      </div>

      {/* PC: ナビ＋アイコン */}
      <div className="hidden md:flex md:flex-1 md:items-stretch md:justify-end">
        <nav
          className="flex md:items-stretch md:gap-club-24"
          aria-label="メインナビゲーション"
        >
          {navItems.map(({ label, href }) => (
            <Link
              key={href}
              href={href}
              className={cn(
                PC_NAV_ITEM_HOVER_CLASS,
                'text-club-p-small md:text-club-p-small-pc text-club-gray-600 hover:text-club-gray-800 transition-colors',
              )}
            >
              {label}
            </Link>
          ))}
          <div className="flex items-stretch gap-club-8">
            <HeaderShopIcon className={PC_NAV_ITEM_HOVER_CLASS} />
            {onPcMyPageMenuEnter && onPcMyPageMenuLeave && (
              <PcMyPageMenuTrigger
                isOpen={isPcMyPageMenuOpen}
                onMouseEnter={onPcMyPageMenuEnter}
                onMouseLeave={onPcMyPageMenuLeave}
                className={PC_NAV_ITEM_HOVER_CLASS}
              />
            )}
          </div>
        </nav>
      </div>

      {/* SP: 上段3カラム */}
      <div className="w-full md:hidden">
        <div className="grid grid-cols-3 items-center min-w-0">
          {/* 左: ハンバーガー */}
          <div className="flex items-center justify-start overflow-visible">
            {!isPcMyPageMenuOpen && (
              <button
                type="button"
                className="relative flex h-8 w-8 items-center justify-center p-0 text-club-gray-600 overflow-visible"
                aria-label={
                  isSpMenuOpen ? 'メニューを閉じる' : 'メニューを開く'
                }
                aria-expanded={isSpMenuOpen}
                onClick={onToggleSpMenu}
              >
                <HamburgerIcon open={isSpMenuOpen} />
              </button>
            )}
          </div>
          <div className="min-w-0 flex items-center justify-center">
            <HeaderLogo imageClassName="h-4 w-auto" />
          </div>
          {/* 右: ショップ・マイページ or Close */}
          <div className="min-w-0 flex items-center justify-end">
            {isPcMyPageMenuOpen && onCloseSpMyPageMenu ? (
              <HeaderCloseIcon onClick={onCloseSpMyPageMenu} />
            ) : (
              <>
                <HeaderShopIcon />
                <HeaderPersonIcon onClick={onSpMyPageMenuClick} />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
